// REFACTOR: need to refactor this whole, states are not handled correctly, methods are not usable for controlled calls,
// duplicate states like selectedDate and metadata, incorrect naming convention metadata is selectedDate but actually selected date is inside metadata
// hardcoded logic for section extracting using it's name.
import { Button } from "@/components/ui/button";
import DateRangeDropdown, {
  DateRangeOption,
} from "./components/dropdowns/DateRangeDropdown";
import AccountOverview from "./account-overview/AccountOverview";
import { IconRefresh } from "@tabler/icons-react";
import CreativeOverview from "./creatives-overview/CreativeOverview";
import NewlyLaunchedOverview from "./newly-launched-overview";
import AllTimeCreativeLeaderboard from "./all-time-leaderboard/AllTimeCreativeLeaderboard";
import {
  AccountOverviewData,
  AllTimeCreativeLeaderboardData,
  CreativeOverviewData,
  CreativeReport,
  CreativeReportColumn,
  CreativeReportData,
  NewlyLaunchedOverviewData,
} from "../../types";
import { useCallback, useEffect, useState, useRef } from "react";
import {
  clearPlatformCache,
  fetchCreativeReportData,
  syncReport,
} from "../../apiService";
import { useGlobalStore } from "@/GlobalStore";
import axios, { CancelTokenSource } from "axios";
import WaveLoader from "@/components/common/loaders/WaveLoader";
import { TimeInterval } from "@/types";
import { useReportCache } from "../../hooks/useReportCache";
import ErrorState from "@/components/common/errorStates/ErrorState";
import NothingToShow from "@/components/common/emptyStates/NothingToShow";
import { getPresetDateRanges } from "./components/dropdowns/DateRangeDropdown";
import {
  computeAutoCompareRange,
  formatForAPI,
  getDefaultSevenDay,
} from "@/utils/dateUtils";
import { TZDate } from "@date-fns/tz";
import { fromZonedTime } from "date-fns-tz";
import { RiChatSmileAiLine, RiGitForkLine } from "@remixicon/react";
import AdAccountOverview from "./ad-account-overview";
import Filters, { FiltersCustomTrigger } from "@/components/common/filters/Filters";
import {
  SectionFilter,
  CreativeReportMetadata,
  CreativeSectionConfiguration,
  ReportDate,
} from "@/types";
import { useParams } from "react-router-dom";

interface Props {
  reportConfig: CreativeReport | null;
  reportConfigLoading: boolean;
  isChatOpen: boolean;
  setIsChatOpen: (isChatOpen: boolean) => void;
  reportConfigError: boolean;
  allowedDateRange: {
    first_allowed_date: TZDate;
    last_allowed_date: TZDate;
  } | null;
}

const Overview = ({
  reportConfig,
  reportConfigLoading,
  isChatOpen,
  setIsChatOpen,
  reportConfigError,
  allowedDateRange,
}: Props) => {
  const {
    selectedAccount,
    isCreativeListLoading,
    selectedCreativeReport,
    sidebarCollapsed,
  } = useGlobalStore();
  const { accountId } = useParams();
  const isMergedAccount = selectedAccount?.type === "MERGED";
  const isV2 = selectedAccount?.version === "v2";
  const [sectionData, setSectionData] = useState<Record<string, CreativeReportData>>({});
  const [sectionLoadingStates, setSectionLoadingStates] = useState<
    Record<string, boolean>
  >({});
  const [sectionErrorStates, setSectionErrorStates] = useState<
    Record<string, { hasError: boolean; message?: string }>
  >({});
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOption | null>(
    null,
  );
  const [defaultMetricId, setDefaultMetricId] = useState<string>();
  const [isReportSyncing, setIsReportSyncing] = useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useState<SectionFilter[]>([]);
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [advancedTempFilter, setAdvancedTempFilter] = useState<SectionFilter | undefined>(
    undefined,
  );
  const advancedOpenedAtRef = useRef<number>(0);

  const adAccountOverviewSectionId = isMergedAccount
    ? reportConfig?.sections.find(
        (s) => s.configuration?.section_type === "dashboard_ad_account_overview",
      )?.id
    : undefined;

  // Get section IDs early so they can be used in fetchSectionData dependencies
  const accountOverviewSectionId = reportConfig?.sections.find(
    (section) => section.configuration?.section_type === "dashboard_overview",
  )?.id;

  const creativeOverviewSectionId = reportConfig?.sections.find(
    (section) => section.configuration?.section_type === "dashboard_trends",
  )?.id;

  const newlyLaunchedSectionId = reportConfig?.sections.find(
    (section) => section.configuration?.section_type === "dashboard_new_creatives",
  )?.id;

  const allTimeLeaderboardSectionId = reportConfig?.sections.find(
    (section) => section.configuration?.section_type === "dashboard_leaderboard",
  )?.id;

  // Refs to store cancel tokens and timeouts
  const cancelTokensRef = useRef<Record<string, CancelTokenSource>>({});
  const timeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Initialize cache hook
  const { checkCache, saveToCache, clearCacheForReport } = useReportCache();

  // Helper function to create date config from DateRangeOption
  const createDateConfig = useCallback(
    (dateRange: DateRangeOption): ReportDate => {
      return {
        label: dateRange.label,
        start_date: formatForAPI(
          dateRange.value.startDate,
          selectedAccount?.time_zone || "UTC",
        ),
        end_date: formatForAPI(
          dateRange.value.endDate,
          selectedAccount?.time_zone || "UTC",
        ),
      };
    },
    [selectedAccount?.time_zone],
  );

  // Helper function to create compare_date config from DateRangeOption's compareValue
  const createCompareDateConfig = useCallback(
    (compareValue: DateRangeOption["compareValue"]): ReportDate | null => {
      if (!compareValue) return null;
      return {
        label: TimeInterval.CUSTOM,
        start_date: formatForAPI(
          compareValue.startDate,
          selectedAccount?.time_zone || "UTC",
        ),
        end_date: formatForAPI(compareValue.endDate, selectedAccount?.time_zone || "UTC"),
      };
    },
    [selectedAccount?.time_zone],
  );

  const handleRetry = async (
    sectionId: string,
    defaultMetricId?: string,
    error_type?: string,
  ) => {
    if (
      accountId &&
      error_type === "retry" &&
      selectedAccount?.id &&
      selectedDateRange &&
      reportConfig
    ) {
      // Get the section configuration (same pattern as fetchSectionData)
      let sectionConfiguration = reportConfig?.sections.find(
        (section) => section.id === sectionId,
      )?.configuration;

      // Build date and compare_date objects for section configuration
      // Always use current date from selectedDateRange for API calls (user's current selection takes precedence)
      const dateForConfig = createDateConfig(selectedDateRange);
      const compareDateForConfig = createCompareDateConfig(
        selectedDateRange.compareValue,
      );

      // Exclude filters from section configuration (filters stay at report level)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { filters: _filters, ...sectionConfigWithoutFilters } =
        sectionConfiguration || {};

      // Always include current date and compare_date in section configuration for API calls
      // This ensures API uses the current date even if section has old date
      sectionConfiguration = {
        ...sectionConfigWithoutFilters,
        date: dateForConfig,
        compare_date: compareDateForConfig,
        column_choices: {
          ...sectionConfigWithoutFilters?.column_choices,
        },
        ...(defaultMetricId && { kpi_metric_id: defaultMetricId }),
      };

      // Combine section configuration and report metadata for cache clearing
      const cacheData: CreativeSectionConfiguration & CreativeReportMetadata = {
        ...sectionConfiguration,
        ...reportConfig?.metadata,
        report_filters: selectedFilters,
      };
      try {
        await clearPlatformCache(accountId, cacheData);
      } catch (error) {
        console.error("Error clearing platform cache during retry:", error);
      }
    }
    await fetchSectionData(sectionId, defaultMetricId);
  };

  const fetchSectionData = useCallback(
    async (
      sectionId: string,
      overrideMetricId?: string,
      isPolling?: boolean,
      operationMode?: "update_only" | "fetch_only" | "both",
      metricIds?: string[],
      overrideFilters?: SectionFilter[],
    ) => {
      if (!selectedAccount?.id || !selectedDateRange || !reportConfig?.id) return;
      // Create new cancel token for this request
      const cancelToken = axios.CancelToken.source();

      try {
        // Cancel previous request for this section if it exists
        if (cancelTokensRef.current[sectionId]) {
          cancelTokensRef.current[sectionId].cancel("New request initiated");
        }
        cancelTokensRef.current[sectionId] = cancelToken;

        // Set loading state for this section and clear any previous errors
        if (!isPolling && operationMode !== "update_only") {
          setSectionLoadingStates((prev) => ({ ...prev, [sectionId]: true }));
        }
        if (operationMode !== "update_only") {
          setSectionErrorStates((prev) => ({
            ...prev,
            [sectionId]: { hasError: false },
          }));
        }

        // Get the section configuration
        let sectionConfiguration = reportConfig?.sections.find(
          (section) => section.id === sectionId,
        )?.configuration;

        // Build date and compare_date objects for section configuration using helper functions
        // Always use current date from selectedDateRange for API calls (user's current selection takes precedence)
        const dateForConfig = createDateConfig(selectedDateRange);
        const compareDateForConfig = createCompareDateConfig(
          selectedDateRange.compareValue,
        );

        // Exclude filters from section configuration (filters stay at report level)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { filters: _filters, ...sectionConfigWithoutFilters } =
          sectionConfiguration || {};

        // Always include current date and compare_date in section configuration for API calls
        // This ensures API uses the current date even if section has old date
        sectionConfiguration = {
          ...sectionConfigWithoutFilters,
          date: dateForConfig,
          compare_date: compareDateForConfig,
          column_choices: {
            ...sectionConfigWithoutFilters?.column_choices,
            ...((metricIds !== undefined || metricIds !== null) &&
              Array.isArray(metricIds) && {
                metric: metricIds,
              }),
          },
          ...(overrideMetricId && { kpi_metric_id: overrideMetricId }),
        };

        // Create report metadata with filters and date (filters and date stay at report level, not section level)
        // Always include current date in reportData so cache uses the correct date even if section has old date
        const reportData: CreativeReportMetadata = {
          // Include current date and compare_date so cache key uses the correct date
          date: createDateConfig(selectedDateRange),
          compare_date: createCompareDateConfig(selectedDateRange.compareValue),
          // Include current filters in report metadata (use overrideFilters if provided, otherwise selectedFilters)
          report_filters: overrideFilters ?? selectedFilters,
        };

        let cachedData: CreativeReportData | null = null;

        // Check cache first (skip for v2)
        try {
          if (operationMode !== "update_only" && !isV2) {
            cachedData = await checkCache(
              selectedAccount.id,
              reportConfig.id,
              sectionId,
              sectionConfiguration || {},
              reportData,
            );

            if (cachedData && !isPolling) {
              setSectionData((prev) => ({
                ...prev,
                [sectionId]: cachedData as CreativeReportData,
              }));
              setSectionLoadingStates((prev) => ({ ...prev, [sectionId]: false }));
              setSectionErrorStates((prev) => ({
                ...prev,
                [sectionId]: { hasError: false },
              }));
              // Don't return here - continue to make API call to update cache
            }
          }
        } catch (error) {
          console.error("Error checking cache:", error);
        }

        const data = await fetchCreativeReportData({
          accountId: selectedAccount?.id,
          sectionId,
          sectionData: sectionConfiguration,
          reportData,
          cancelToken: cancelToken.token,
          isPolling,
          operation_mode: operationMode,
          section_level_date: true,
        });

        if (data.status === "pending" || data.status === "partial") {
          if (operationMode !== "update_only") {
            if (data.status === "partial") {
              setSectionLoadingStates((prev) => ({ ...prev, [sectionId]: false }));
            }
            // Clear existing timeout for this section
            if (timeoutsRef.current[sectionId]) {
              clearTimeout(timeoutsRef.current[sectionId]);
            }

            // Set new timeout for retry
            const timeoutId = setTimeout(() => {
              void fetchSectionData(
                sectionId,
                overrideMetricId,
                true,
                operationMode,
                metricIds,
              );
            }, 5000);

            timeoutsRef.current[sectionId] = timeoutId;

            // Update data if not from cache and keep loading/polling state
            if (!cachedData) {
              setSectionData((prev) => ({ ...prev, [sectionId]: data }));
            }
          }
          return data;
        }

        if (data.status === "success") {
          if (operationMode !== "update_only") {
            // Always clear loading states
            setSectionLoadingStates((prev) => ({ ...prev, [sectionId]: false }));
            // Update section data if we didn't already show cached data
            if (!cachedData) {
              setSectionData((prev) => ({ ...prev, [sectionId]: data }));
            }

            // Always clear error state on success
            setSectionErrorStates((prev) => ({
              ...prev,
              [sectionId]: { hasError: false },
            }));

            // Cache the successful data to keep cache fresh (skip for v2)
            if (!isV2) {
              try {
                await saveToCache(
                  selectedAccount.id,
                  reportConfig.id,
                  sectionId,
                  sectionConfiguration || {},
                  data,
                  reportData,
                );
              } catch (error) {
                console.error("Error saving to cache:", error);
              }
            }
          }
        }

        if (data.status === "failed") {
          if (operationMode !== "update_only") {
            setSectionLoadingStates((prev) => ({ ...prev, [sectionId]: false }));
            setSectionErrorStates((prev) => ({
              ...prev,
              [sectionId]: { hasError: true, message: data.message },
            }));
          }
        }

        return data;
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log("Request cancelled:", error.message);
        } else {
          console.error(error);
          if (operationMode !== "update_only") {
            // Clear loading state on API failure
            setSectionLoadingStates((prev) => ({ ...prev, [sectionId]: false }));
            // Set error state on API failure
            setSectionErrorStates((prev) => ({
              ...prev,
              [sectionId]: {
                hasError: true,
                message:
                  error instanceof Error
                    ? error.message
                    : "An error occurred while fetching data",
              },
            }));
          }
        }
        return null;
      } finally {
        // Clean up the cancel token
        if (cancelTokensRef.current[sectionId] === cancelToken) {
          delete cancelTokensRef.current[sectionId];
        }
      }
    },
    [
      selectedAccount?.id,
      selectedDateRange,
      reportConfig?.id,
      reportConfig?.sections,
      selectedFilters,
      checkCache,
      saveToCache,
      createDateConfig,
      createCompareDateConfig,
      isV2,
    ],
  );

  const fetchMultipleSections = useCallback(
    (
      sectionIds: string[],
      overrideMetricId?: string,
      operationMode?: "update_only" | "fetch_only" | "both",
    ) => {
      if (!reportConfig) return;
      try {
        void Promise.all(
          sectionIds.map((sectionId) =>
            fetchSectionData(sectionId, overrideMetricId, false, operationMode),
          ),
        );
      } catch (error) {
        console.error(error);
      }
    },
    [reportConfig, fetchSectionData],
  );

  // Handle date range change
  const handleDateRangeChange = (dateRange: DateRangeOption) => {
    setSelectedDateRange(dateRange);
  };

  // Manual refresh function that cancels all previous requests and clears cache
  const handleManualRefresh = useCallback(async () => {
    if (!reportConfig || !selectedAccount?.id) return;

    setIsReportSyncing(true);
    setSectionLoadingStates((prev) => {
      const keys = Object.keys(prev);
      const newState = Object.fromEntries(keys.map((key) => [key, true]));
      return newState;
    });
    try {
      // Trigger backend sync similar to CreativesReport flow
      await syncReport(selectedAccount.id, reportConfig.id);
    } catch (error) {
      console.error("Error syncing report during manual refresh:", error);
    }

    try {
      // Cancel all pending requests
      Object.values(cancelTokensRef.current).forEach((cancelToken) => {
        cancelToken.cancel("Manual refresh initiated");
      });

      // Clear all timeouts
      Object.values(timeoutsRef.current).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });

      // Clear refs
      cancelTokensRef.current = {};
      timeoutsRef.current = {};

      // Clear cache for this report
      try {
        await clearCacheForReport(selectedAccount.id, reportConfig.id);
      } catch (error) {
        console.error("Error clearing cache during manual refresh:", error);
      }

      // Fetch all sections again
      void fetchMultipleSections(
        reportConfig.sections.map((section) => section.id),
        defaultMetricId,
      );
    } finally {
      setIsReportSyncing(false);
    }
  }, [
    fetchMultipleSections,
    reportConfig,
    selectedAccount?.id,
    clearCacheForReport,
    defaultMetricId,
  ]);

  // Clear data and states when report changes
  useEffect(() => {
    setSectionData({});
    setSectionLoadingStates({});
    setSectionErrorStates({});

    // Clear all timeouts
    Object.values(timeoutsRef.current).forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    timeoutsRef.current = {};

    // Cancel all requests
    Object.values(cancelTokensRef.current).forEach((cancelToken) => {
      cancelToken.cancel("Report changed");
    });
    cancelTokensRef.current = {};
  }, [reportConfig?.id]);

  // Initialize selected date from first section configuration (since dates are now at section level)
  useEffect(() => {
    if (!reportConfig || !reportConfig.sections || reportConfig.sections.length === 0)
      return;

    // Get date from first section's configuration
    const firstSection = reportConfig.sections[0];
    const sectionDate = firstSection?.configuration?.date;
    const sectionCompareDate = firstSection?.configuration?.compare_date;

    if (!sectionDate) return;

    const labelFromSection = sectionDate.label;
    const timezone = selectedAccount?.time_zone || "UTC";
    const now = new TZDate(new Date(), timezone);
    const isLastAllowedDateToday = allowedDateRange?.last_allowed_date
      ? now.toDateString() === allowedDateRange?.last_allowed_date.toDateString()
      : true;
    const isCustom = labelFromSection && labelFromSection === TimeInterval.CUSTOM;
    if (!isLastAllowedDateToday && !isCustom) {
      const defaultSevenDay = getDefaultSevenDay(allowedDateRange, timezone);
      if (defaultSevenDay) {
        const _range: DateRangeOption = {
          key: "custom", // key is 'custom' not '7d' apply properly to calender
          label: labelFromSection ? labelFromSection : TimeInterval.LAST_7_DAYS, // intentional to keep it overview updated to existing label.
          value: { startDate: defaultSevenDay.from, endDate: defaultSevenDay.to },
          compareValue: (() => {
            const main = {
              label: defaultSevenDay.label,
              from: defaultSevenDay.from,
              to: defaultSevenDay.to,
            };
            const cmp = computeAutoCompareRange(main, timezone);
            return cmp ? { startDate: cmp.from, endDate: cmp.to } : null;
          })(),
        };
        setSelectedDateRange(_range);
      }
      return;
    }

    const presets = getPresetDateRanges(timezone);

    switch (labelFromSection) {
      case TimeInterval.LAST_7_DAYS:
        setSelectedDateRange(presets[TimeInterval.LAST_7_DAYS]);
        break;
      case TimeInterval.LAST_14_DAYS:
        setSelectedDateRange(presets[TimeInterval.LAST_14_DAYS]);
        break;
      case TimeInterval.LAST_28_DAYS:
        setSelectedDateRange(presets[TimeInterval.LAST_28_DAYS]);
        break;
      case TimeInterval.LAST_30_DAYS:
        setSelectedDateRange(presets[TimeInterval.LAST_30_DAYS]);
        break;
      default:
        // If custom, construct from section configuration (including optional compare_date)
        if (labelFromSection === TimeInterval.CUSTOM && sectionDate) {
          try {
            const start = new TZDate(
              fromZonedTime(sectionDate.start_date, timezone),
              timezone,
            );
            const end = new TZDate(
              fromZonedTime(sectionDate.end_date, timezone),
              timezone,
            );
            const custom: DateRangeOption = {
              key: "custom",
              label: TimeInterval.CUSTOM,
              value: { startDate: start, endDate: end },
              compareValue: null,
            };

            // Use compare_date from section configuration
            if (sectionCompareDate?.start_date && sectionCompareDate?.end_date) {
              const cStart = new TZDate(
                fromZonedTime(sectionCompareDate.start_date, timezone),
                timezone,
              );
              const cEnd = new TZDate(
                fromZonedTime(sectionCompareDate.end_date, timezone),
                timezone,
              );
              custom.compareValue = { startDate: cStart, endDate: cEnd };
            }
            setSelectedDateRange(custom);
          } catch (e) {
            console.error("Failed to parse custom date from section configuration", e);
          }
        }
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportConfig, selectedAccount?.time_zone]);

  useEffect(() => {
    if (selectedDateRange && reportConfig) {
      const defaultMetricId =
        reportConfig.sections[0]?.configuration?.kpi_metric_id || "";
      setDefaultMetricId(defaultMetricId);
      void fetchMultipleSections(
        reportConfig.sections.map((section) => section.id),
        defaultMetricId,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateRange]);

  const handleSetDefaultMetric = useCallback(
    (metricId: string) => {
      setDefaultMetricId(metricId);
      void fetchMultipleSections(
        reportConfig?.sections.map((section) => section.id) || [],
        metricId,
        "update_only",
      );
    },
    [reportConfig, fetchMultipleSections],
  );

  // Handle report filter change
  const handleReportFilterChange = useCallback(
    async (reportFilters: SectionFilter[] | null) => {
      if (!reportConfig || !selectedAccount?.id || !selectedDateRange) return;

      const newFilters = reportFilters ?? [];
      setSelectedFilters(newFilters);

      // Clear cache for this report since filters have changed
      try {
        await clearCacheForReport(selectedAccount.id, reportConfig.id);
      } catch (error) {
        console.error("Error clearing cache for report filters:", error);
      }

      // Refetch all sections with new filters (filters are in report metadata, not section config)
      try {
        await Promise.all(
          reportConfig.sections.map((section) =>
            fetchSectionData(section.id, undefined, false, "both", undefined, newFilters),
          ),
        );
      } catch (error) {
        console.error("Error fetching sections with new filters:", error);
      }
    },
    [
      reportConfig,
      selectedAccount?.id,
      selectedDateRange,
      clearCacheForReport,
      fetchSectionData,
    ],
  );

  // Initialize selectedFilters from reportConfig metadata
  useEffect(() => {
    if (reportConfig?.metadata?.report_filters) {
      setSelectedFilters(reportConfig.metadata.report_filters);
    } else {
      setSelectedFilters([]);
    }
  }, [reportConfig?.metadata?.report_filters]);

  // Cleanup function to cancel all pending requests and clear timeouts
  useEffect(() => {
    return () => {
      // Cancel all pending requests
      Object.values(cancelTokensRef.current).forEach((cancelToken) => {
        cancelToken.cancel("Component unmounted");
      });

      // Clear all timeouts
      Object.values(timeoutsRef.current).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });

      // Clear refs
      cancelTokensRef.current = {};
      timeoutsRef.current = {};
    };
  }, []);

  const dataConfigurations = useGlobalStore((s) => s.dataConfigurations);

  if (reportConfigLoading || isCreativeListLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <WaveLoader />
      </div>
    );
  }
  if (reportConfig?.sections.length === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <NothingToShow />
      </div>
    );
  }
  if (reportConfigError) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <ErrorState />
      </div>
    );
  }

  // ensuring date range is initialized
  if (!reportConfig || !selectedDateRange) {
    return null;
  }

  return (
    <div className="h-full w-full flex flex-col gap-[8px] pt-[8px]">
      <div className="flex items-center justify-between px-[10px]">
        <div className="flex items-center gap-[6px]">
          {sidebarCollapsed && <div className="h-[32px] w-[32px]"></div>}
          <DateRangeDropdown
            selectedRange={selectedDateRange}
            onRangeChange={handleDateRangeChange}
            allowedDateRange={allowedDateRange}
          />
          <Button
            variant="sq-outline"
            size="icon"
            onClick={() => void handleManualRefresh()}
            className="shadow-sm"
            disabled={isReportSyncing}
          >
            <IconRefresh size={14} className="text-brand-primary-400" />
          </Button>
        </div>
        <div className="flex gap-2">
          {dataConfigurations?.filter && (
            <Filters
              selectedFilters={selectedFilters}
              onFilterChange={(filters) => void handleReportFilterChange(filters)}
              selectedDate={reportConfig?.metadata?.date ?? null}
              dataConfigFilter={dataConfigurations?.filter}
              columnChoices={dataConfigurations?.column_choices}
              showSelectedFilters={false}
              showAdvancedFilter={showAdvancedFilter}
              onShowAdvancedFilterChange={setShowAdvancedFilter}
              advancedTempFilter={advancedTempFilter}
              onAdvancedTempFilterChange={setAdvancedTempFilter}
              advancedOpenedAtRef={advancedOpenedAtRef}
              customTrigger={<FiltersCustomTrigger active={!!selectedFilters?.length} />}
              clearSelection
            />
          )}
          <div
            onClick={() =>
              !isChatOpen && selectedCreativeReport?.id && setIsChatOpen(true)
            }
            className={`${isChatOpen || !selectedCreativeReport?.id ? "hidden" : ""}`}
          >
            <div
              className={`h-[32px] flex items-center gap-[6px] px-[12px] py-[6px] squircle-mask squircle-radius-[8px] squircle-smooth-[1]  bg-gradient-to-r from-[#FF4500]/90 to-[#FF5188]/90 ${
                isChatOpen || !selectedCreativeReport?.id
                  ? "cursor-not-allowed opacity-0 hidden"
                  : "cursor-pointer hover:text-accent-foreground"
              }`}
            >
              <RiChatSmileAiLine size={16} className="flex-shrink-0 text-white" />
              <span className="text-white text-[12px] font-medium tracking-[0.24px] relative top-[1px] whitespace-nowrap">
                Ask AI
              </span>
            </div>
          </div>
        </div>
      </div>
      {((selectedFilters && selectedFilters.length > 0) || showAdvancedFilter) && (
        <div className="pl-[9px]">
          {dataConfigurations?.filter && (
            <Filters
              selectedFilters={selectedFilters}
              onFilterChange={(filters) => void handleReportFilterChange(filters)}
              selectedDate={reportConfig?.metadata?.date ?? null}
              dataConfigFilter={dataConfigurations?.filter}
              columnChoices={dataConfigurations?.column_choices}
              showAddFilters={false}
              showAdvancedFilter={showAdvancedFilter}
              onShowAdvancedFilterChange={setShowAdvancedFilter}
              advancedTempFilter={advancedTempFilter}
              onAdvancedTempFilterChange={setAdvancedTempFilter}
              advancedOpenedAtRef={advancedOpenedAtRef}
            />
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col gap-4 pb-2 px-[10px] pt-[12px]">
        {/* Account Overview Section */}
        {accountOverviewSectionId && (
          <AccountOverview
            data={
              sectionData[accountOverviewSectionId ?? ""]
                ?.data as unknown as AccountOverviewData
            }
            status={sectionData[accountOverviewSectionId ?? ""]?.status}
            loading={sectionLoadingStates[accountOverviewSectionId ?? ""]}
            error={sectionErrorStates[accountOverviewSectionId ?? ""]}
            defaultMetricId={defaultMetricId}
            onSetDefaultMetric={handleSetDefaultMetric}
            onRetry={() =>
              void handleRetry(
                accountOverviewSectionId,
                defaultMetricId,
                sectionData[accountOverviewSectionId ?? ""]?.error_type,
              )
            }
            title={
              isMergedAccount
                ? "Combined Account Overview"
                : sectionData[accountOverviewSectionId ?? ""]?.section?.title
            }
            icon={RiGitForkLine}
            sectionId={accountOverviewSectionId}
            configWithReportMetadata={{
              ...(sectionData[accountOverviewSectionId ?? ""]?.section?.configuration ??
                {}),
              ...reportConfig?.metadata,
              // Always use current date from selectedDateRange (user's current selection takes precedence)
              ...(selectedDateRange && {
                date: createDateConfig(selectedDateRange),
              }),
              ...(selectedDateRange && {
                compare_date: createCompareDateConfig(selectedDateRange.compareValue),
              }),
              // Always use current filters from selectedFilters
              report_filters: selectedFilters,
              column_choices: {
                metric: Array.from(
                  new Set([
                    ...((
                      sectionData[accountOverviewSectionId ?? ""]
                        ?.data as unknown as AccountOverviewData
                    )?.overview_metrics_data?.creative_metric_columns?.map(
                      (m) => m.key,
                    ) ?? []),
                    ...((
                      sectionData[accountOverviewSectionId ?? ""]
                        ?.data as unknown as AccountOverviewData
                    )?.overview_metrics_data?.columns?.map((m) => m.key) ?? []),
                  ]),
                ),
              },
            }}
          />
        )}
        {adAccountOverviewSectionId && (
          <AdAccountOverview
            reportMetadata={{
              ...reportConfig?.metadata,
              // Always use current date from selectedDateRange (user's current selection takes precedence)
              ...(selectedDateRange && {
                date: createDateConfig(selectedDateRange),
              }),
              ...(selectedDateRange && {
                compare_date: createCompareDateConfig(selectedDateRange.compareValue),
              }),
              // Always use current filters from selectedFilters
              report_filters: selectedFilters,
            }}
            reportData={sectionData[adAccountOverviewSectionId]}
            title={sectionData[adAccountOverviewSectionId]?.section?.title ?? ""}
            status={sectionData[adAccountOverviewSectionId]?.status}
            loading={sectionLoadingStates[adAccountOverviewSectionId]}
            error={sectionErrorStates[adAccountOverviewSectionId]}
            onRetry={() =>
              void handleRetry(
                adAccountOverviewSectionId,
                undefined,
                sectionData[adAccountOverviewSectionId]?.error_type,
              )
            }
            onMetricChange={(metrics) => {
              const metricsOptions = useGlobalStore
                .getState()
                .dataConfigurations?.column_choices.find(
                  (c) => c.key === "metric",
                )?.options;

              const currentData = sectionData[adAccountOverviewSectionId];
              const previousMetricIds =
                currentData?.section?.configuration?.column_choices?.["metric"] ?? [];

              // Find added and removed metrics
              const addedMetrics = metrics.filter(
                (metric) => !previousMetricIds.includes(metric),
              );
              const removedMetrics = previousMetricIds.filter(
                (metric) => !metrics.includes(metric),
              );

              setSectionData((prev) => {
                const currentSectionData = prev[adAccountOverviewSectionId];
                if (
                  !currentSectionData ||
                  !currentSectionData.section ||
                  !currentSectionData.data
                ) {
                  return prev;
                }

                return {
                  ...prev,
                  [adAccountOverviewSectionId]: {
                    ...currentSectionData,
                    section: {
                      ...currentSectionData.section,
                      configuration: {
                        ...currentSectionData.section.configuration,
                        column_choices: {
                          ...currentSectionData.section.configuration.column_choices,
                          metric: metrics,
                        },
                      },
                    },
                    status: "partial",
                    data: {
                      ...currentSectionData.data,
                      status: "partial",
                      columns: (() => {
                        // Get all columns except the ones we're removing
                        const filteredColumns = currentSectionData.data.columns.filter(
                          (col: CreativeReportColumn) =>
                            !removedMetrics.includes(col.key),
                        );

                        // Add new columns for added metrics
                        const newColumns: CreativeReportColumn[] = addedMetrics.map(
                          (metricKey) => ({
                            key: metricKey,
                            label:
                              metricsOptions?.find((opt) => opt.key === metricKey)
                                ?.label || metricKey,
                            column_type: "number",
                          }),
                        );

                        // Combine existing columns + new columns
                        const allColumns = [...filteredColumns, ...newColumns];

                        // Reorder metrics according to the new order
                        const nonMetricColumns = allColumns.filter(
                          (col: CreativeReportColumn) => !metrics.includes(col.key),
                        );
                        const metricColumns = metrics
                          .map((metricKey) =>
                            allColumns.find(
                              (col: CreativeReportColumn) => col.key === metricKey,
                            ),
                          )
                          .filter(Boolean) as CreativeReportColumn[];

                        return [...nonMetricColumns, ...metricColumns];
                      })(),
                      rows: currentSectionData.data.rows?.map((row) => {
                        const updatedRow = { ...row };

                        // Set loading values for added metrics
                        addedMetrics.forEach((metricKey) => {
                          updatedRow[metricKey] = "__LOADING__";
                        });

                        return updatedRow;
                      }),
                      total: (() => {
                        const updatedTotal = { ...currentSectionData.data.total };
                        addedMetrics.forEach((metricKey) => {
                          updatedTotal[metricKey] = "__LOADING__";
                        });
                        return updatedTotal;
                      })(),
                    },
                  },
                };
              });

              void fetchSectionData(
                adAccountOverviewSectionId ?? "",
                defaultMetricId,
                false,
                "both",
                metrics,
              );
            }}
          />
        )}

        {/* Creative Overview Section */}
        {creativeOverviewSectionId && (
          <CreativeOverview
            data={
              sectionData[creativeOverviewSectionId ?? ""]
                ?.data as unknown as CreativeOverviewData
            }
            title={sectionData[creativeOverviewSectionId ?? ""]?.section?.title ?? ""}
            loading={sectionLoadingStates[creativeOverviewSectionId ?? ""]}
            error={sectionErrorStates[creativeOverviewSectionId ?? ""]}
            defaultMetricId={defaultMetricId}
            onSetDefaultMetric={handleSetDefaultMetric}
            onRetry={() =>
              void handleRetry(
                creativeOverviewSectionId,
                defaultMetricId,
                sectionData[creativeOverviewSectionId ?? ""]?.error_type,
              )
            }
            reportMetadata={{
              ...reportConfig?.metadata,
              // Always use current date from selectedDateRange (user's current selection takes precedence)
              ...(selectedDateRange && {
                date: createDateConfig(selectedDateRange),
              }),
              ...(selectedDateRange && {
                compare_date: createCompareDateConfig(selectedDateRange.compareValue),
              }),
              // Always use current filters from selectedFilters
              report_filters: selectedFilters,
            }}
          />
        )}

        {/* Newly Launched Overview Section */}
        {newlyLaunchedSectionId && (
          <NewlyLaunchedOverview
            data={
              sectionData[newlyLaunchedSectionId ?? ""]
                ?.data as unknown as NewlyLaunchedOverviewData
            }
            title={sectionData[newlyLaunchedSectionId ?? ""]?.section?.title ?? ""}
            loading={sectionLoadingStates[newlyLaunchedSectionId ?? ""]}
            error={sectionErrorStates[newlyLaunchedSectionId ?? ""]}
            defaultMetricId={defaultMetricId}
            onRetry={() =>
              void handleRetry(
                newlyLaunchedSectionId,
                defaultMetricId,
                sectionData[newlyLaunchedSectionId ?? ""]?.error_type,
              )
            }
          />
        )}

        {/* All-Time Creative Leaderboard Section */}
        {allTimeLeaderboardSectionId && (
          <AllTimeCreativeLeaderboard
            reportMetadata={{
              ...reportConfig?.metadata,
              // Always use current date from selectedDateRange (user's current selection takes precedence)
              ...(selectedDateRange && {
                date: createDateConfig(selectedDateRange),
              }),
              ...(selectedDateRange && {
                compare_date: createCompareDateConfig(selectedDateRange.compareValue),
              }),
              // Always use current filters from selectedFilters
              report_filters: selectedFilters,
            }}
            data={
              sectionData[allTimeLeaderboardSectionId ?? ""]
                ?.data as unknown as AllTimeCreativeLeaderboardData
            }
            title={sectionData[allTimeLeaderboardSectionId ?? ""]?.section?.title ?? ""}
            loading={sectionLoadingStates[allTimeLeaderboardSectionId ?? ""]}
            error={sectionErrorStates[allTimeLeaderboardSectionId ?? ""]}
            defaultMetricId={defaultMetricId}
            onRetry={() =>
              void handleRetry(
                allTimeLeaderboardSectionId,
                defaultMetricId,
                sectionData[allTimeLeaderboardSectionId ?? ""]?.error_type,
              )
            }
            onMetricChange={(metricIds) => {
              const sectionConfiguration = reportConfig?.sections.find(
                (section) => section.id === allTimeLeaderboardSectionId,
              )?.configuration;

              // Build date and compare_date for section configuration
              // Always use current date from selectedDateRange for API calls (user's current selection takes precedence)
              const dateForConfig = selectedDateRange
                ? createDateConfig(selectedDateRange)
                : undefined;
              const compareDateForConfig = createCompareDateConfig(
                selectedDateRange?.compareValue,
              );

              // Exclude filters from section configuration (filters stay at report level)
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { filters: _filters, ...sectionConfigWithoutFilters } =
                sectionConfiguration || {};

              // Always include current date and compare_date in section configuration for API calls
              // This ensures API uses the current date even if section has old date
              const updatedSectionConfiguration = {
                ...sectionConfigWithoutFilters,
                ...(dateForConfig && { date: dateForConfig }),
                ...(compareDateForConfig && { compare_date: compareDateForConfig }),
                column_choices: {
                  ...sectionConfigWithoutFilters?.column_choices,
                  ...((metricIds !== undefined || metricIds !== null) &&
                    Array.isArray(metricIds) && {
                      metric: metricIds,
                    }),
                },
                ...(defaultMetricId && { kpi_metric_id: defaultMetricId }),
              };

              // updating section data and cache
              setSectionData((prev) => {
                const updated = { ...prev };
                if (allTimeLeaderboardSectionId) {
                  const data = sectionData[allTimeLeaderboardSectionId ?? ""]?.data;
                  // updating data columns
                  if (data?.columns) {
                    const metricColumns = data?.creative_metric_columns;
                    // Keep non-metric columns
                    const nonMetricColumns = data?.columns.filter(
                      (col) => !metricColumns?.some((m) => m.key === col.key),
                    );
                    // Get the columns that should be displayed based on newMetrics, preserving order
                    const metricsToShow = metricIds
                      .map((metricKey) => metricColumns?.find((m) => m.key === metricKey))
                      .filter((metric): metric is NonNullable<typeof metric> => !!metric);
                    data.columns = [
                      ...(nonMetricColumns ?? []),
                      ...metricsToShow,
                    ] as unknown as CreativeReportColumn[];
                  }
                  updated[allTimeLeaderboardSectionId] = {
                    ...prev[allTimeLeaderboardSectionId],
                    data: data,
                  };
                }
                // caching (skip for v2)
                if (selectedDateRange && selectedAccount?.id && !isV2) {
                  const reportDataForCache = {
                    ...reportConfig?.metadata,
                    date: createDateConfig(selectedDateRange),
                    compare_date: createCompareDateConfig(selectedDateRange.compareValue),
                    report_filters: selectedFilters,
                  };

                  void saveToCache(
                    selectedAccount?.id,
                    reportConfig.id,
                    allTimeLeaderboardSectionId,
                    updatedSectionConfiguration,
                    updated[allTimeLeaderboardSectionId],
                    reportDataForCache,
                  );
                }
                return updated;
              });

              if (selectedAccount?.id && allTimeLeaderboardSectionId)
                void fetchCreativeReportData({
                  accountId: selectedAccount?.id,
                  sectionId: allTimeLeaderboardSectionId,
                  sectionData: updatedSectionConfiguration,
                  reportData: {
                    ...reportConfig.metadata,
                    date: createDateConfig(selectedDateRange),
                    compare_date: createCompareDateConfig(selectedDateRange.compareValue),
                    report_filters: selectedFilters,
                  },
                  cancelToken: undefined,
                  isPolling: false,
                  operation_mode: "update_only",
                  section_level_date: true,
                });
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Overview;
